import { Box, CircularProgress, Typography } from "@mui/material"
import React, { useCallback, useEffect, useState } from "react"
import ScholarshipApplicationsList from "./ScholarshipApplicationList"
import ScholarshipApplicationScreenHeader from "./ScholarshipApplicationScreenHeader"
import type { PaginationProps, ScholarshipApplicationDetailsProps } from "../../typings/scholarship-applications"
import api from "../../api/axiosInstance"
import { PAGINATION_VALUES } from "../../data/pagination"

const SEARCH_FIELDS = "cnic_number,name"

const ScholarshipApplications = () => {
  const [applications, setApplications] = useState<ScholarshipApplicationDetailsProps[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationProps>(PAGINATION_VALUES)
  const [searchTerm, setSearchTerm] = useState<string>("")
  // Total unfiltered application count for the current user — kept in sync
  // only when the search box is empty, so a zero-result search never masks
  // the fact that the student already has an application on file.
  const [totalApplicationCount, setTotalApplicationCount] = useState<number | null>(null)
  // Id of the student's root (parent-less) application, used by the header
  // to route the "Reapply" button to the correct scholarship history trail.
  const [rootApplicationId, setRootApplicationId] = useState<number | null>(null)

  const fetchApplications = useCallback(async (search: string, page: number, pageSize: number) => {
    try {
      setLoading(true)
      const params: Record<string, string | number> = {
        page_no: page,
        page_size: pageSize,
      }

      if (search.trim()) {
        params.search_str = search.trim()
        params.search_fields = SEARCH_FIELDS
      }

      const response = await api.get("/scholarship-applications", { params })
      const results: ScholarshipApplicationDetailsProps[] = response.data.data ?? []
      setApplications(results)
      setPagination(response.data.pagination)
      if (!search.trim()) {
        setTotalApplicationCount(response.data.pagination?.total_no_of_records ?? 0)
        const root = results.find((app) => !app.parent_application)
        if (root) setRootApplicationId(root.id)
      }
      setError(null)
    } catch (err) {
      setError("Failed to fetch applications")
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshApplications = useCallback(async () => {
    try {
      setLoading(true)
      const params: Record<string, string | number> = {
        page_no: pagination.page_number,
        page_size: pagination.page_size,
      }

      if (searchTerm.trim()) {
        params.search_str = searchTerm.trim()
        params.search_fields = SEARCH_FIELDS
      }

      const response = await api.get("/scholarship-applications", { params })
      const results: ScholarshipApplicationDetailsProps[] = response.data.data ?? []
      setApplications(results)
      setPagination(response.data.pagination)
      if (!searchTerm.trim()) {
        setTotalApplicationCount(response.data.pagination?.total_no_of_records ?? 0)
        const root = results.find((app) => !app.parent_application)
        if (root) setRootApplicationId(root.id)
      }
    } catch (err) {
      console.error("Failed to refresh applications:", err)
    } finally {
      setLoading(false)
    }
  }, [pagination.page_number, pagination.page_size, searchTerm])

  // Re-fetch whenever the (already debounced) search term or pagination changes
  useEffect(() => {
    fetchApplications(searchTerm, pagination.page_number, pagination.page_size)
  }, [searchTerm, pagination.page_number, pagination.page_size, fetchApplications])

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term)
    // Reset to first page on new search
    setPagination((prev) => {
      if (prev.page_number === 1) return prev
      return { ...prev, page_number: 1 }
    })
  }, [])

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPagination((p) => ({ ...p, page_number: newPage + 1 })) // MUI is 0-based
  }, [])

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({
      ...prev,
      page_number: 1,
      page_size: Number.parseInt(event.target.value, 10),
    }))
  }, [])

  // Only show a full-page error — never a full-page loading spinner
  // (loading is passed down to the list so the header always stays mounted)
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header always stays rendered — search box never unmounts */}
      <ScholarshipApplicationScreenHeader
        onSearch={handleSearch}
        totalApplicationCount={totalApplicationCount}
        rootApplicationId={rootApplicationId}
      />

      <ScholarshipApplicationsList
        applications={applications}
        pagination={pagination}
        loading={loading}
        searchTerm={searchTerm}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRefresh={refreshApplications}
      />

    </Box>
  )
}

export default ScholarshipApplications
